import React, { useState, useCallback } from "react";
import moment from "moment-timezone";
import { cpfMask } from "../services/funcoesAuxiliares";
import Modal from "./modal";
import { Plus, Filter, Eraser, Search, X, User, Hash, CreditCard } from 'lucide-react';

const FiltrarDadosMobile = ({ setError, getCarteiras, activeTab, page, limit, getSearch }) => {
  const hoje = moment().format().split("T")[0];
  const mesPassado = moment().subtract(1, "month").format();
  const MesAnterior = mesPassado.split("T")[0];

  const { name } = JSON.parse(localStorage.getItem("user"));

  const [modalVisivel, setModalVisivel] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [filtro, setFiltroState] = useState({
    tipo: "cpf",
    valor: ""
  });

  const formattedCodigoUso = useCallback((value) => {
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 7)}.${numbers.slice(7, 10)}`;
    }
  }, []);

  const onChangeTipo = (e) => {
    setFiltroState({
      tipo: e.target.value,
      valor: ""
    });
  };

  const onChangeValor = (e) => {
    const { value } = e.target;
    let valorMascarado = value;

    if (filtro.tipo === "cpf") valorMascarado = cpfMask(value);
    if (filtro.tipo === "codigoUso") valorMascarado = formattedCodigoUso(value);

    setFiltroState((prev) => ({
      ...prev,
      valor: valorMascarado
    }));
  };

  const filtrar = useCallback(async () => {
    setFiltroAtivo(true);
    setIsLoading(true);

    try {
      const filterParams = {
        [filtro.tipo]: filtro.valor,
        gestores: [{ label: name, value: name, id: 1 }].map((c) => c.id),
        selectedStatus: [],
        dataInicial: MesAnterior,
        dataFinal: hoje,
        vendedores: [],
        tags: [],
      };
      await getCarteiras(activeTab, filterParams, page, limit);
      setError(false);
    } catch (error) {
      console.error('FiltrarDadosMobile - erro:', error);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [filtro, getCarteiras, activeTab, page, limit, name, MesAnterior, hoje, setError]);

  const limparFiltro = useCallback(async () => {
    setIsLoading(true);
    try {
      setFiltroAtivo(false);
      setFiltroState({ tipo: "cpf", valor: "" });
      setError(false);
      await getCarteiras(activeTab, null, 1, 10);
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [getCarteiras, activeTab, setError]);

  return (
    <section className="mt-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-lg border border-blue-200">
      {/* Header do filtro mobile */}
      <div className="flex items-center justify-between p-4 border-b border-blue-200">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">Filtro Rápido</h3>
          {filtroAtivo && (
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              Ativo
            </span>
          )}
        </div>
        <button
          className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
          onClick={() => setModalVisivel(true)}
          disabled={isLoading}
          title="Mais opções"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Formulário de filtro */}
      <div className="p-4 space-y-4">
        <form className="space-y-4">
          {/* Seleção do tipo de filtro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="w-4 h-4 inline mr-1" />
              Tipo de Busca
            </label>
            <select 
              id="filtroTipo" 
              onChange={onChangeTipo} 
              disabled={filtroAtivo || isLoading} 
              value={filtro.tipo} 
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white text-gray-700 font-medium"
            >
              <option value="cpf">CPF</option>
              <option value="codigoUso">Código de Uso</option>
              <option value="name">Nome</option>
            </select>
          </div>

          {/* Campo de entrada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {filtro.tipo === "cpf" ? (
                <CreditCard className="w-4 h-4 inline mr-1" />
              ) : filtro.tipo === "codigoUso" ? (
                <Hash className="w-4 h-4 inline mr-1" />
              ) : (
                <User className="w-4 h-4 inline mr-1" />
              )}
              {filtro.tipo === "cpf" ? "CPF" : filtro.tipo === "codigoUso" ? "Código de Uso" : "Nome do Aluno"}
            </label>
            <div className="relative">
              <input
                type="text"
                id={`filtro-${filtro.tipo}`}
                value={filtro.valor}
                onChange={onChangeValor}
                placeholder={
                  filtro.tipo === "cpf"
                    ? "Digite o CPF..."
                    : filtro.tipo === "codigoUso"
                    ? "Digite o código de uso..."
                    : "Digite o nome do aluno..."
                }
                disabled={isLoading}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white text-gray-700"
              />
              {filtro.valor && (
                <button
                  type="button"
                  onClick={() => setFiltroState(prev => ({ ...prev, valor: "" }))}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-3 pt-2">
            {filtroAtivo ? (
              <button 
                type="button"
                onClick={limparFiltro}
                disabled={isLoading}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Eraser className="w-4 h-4" />
                {isLoading ? "Filtrando..." : "Limpar Filtro"}
              </button>
            ) : (
              <button 
                type="button" 
                onClick={filtrar}
                disabled={isLoading || !filtro.valor.trim()}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Filter className="w-4 h-4" />
                {isLoading ? "Limpando..." : "Filtrar"}
              </button>
            )}
          </div>
        </form>

        {/* Modal para mais opções */}
        {modalVisivel && (
          <Modal
            modalVisivel={modalVisivel}
            setModalVisivel={setModalVisivel}
            getCarteiras={getCarteiras}
          />
        )}
      </div>
    </section>
  );
};

export default FiltrarDadosMobile;
